import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Flex,
  Heading,
  Text,
  Badge,
  Grid,
  GridItem,
  Spinner,
  Center,
  Image,
  VStack,
  HStack,
  Divider,
} from "@chakra-ui/react";
import PiketService from "services/report/piketService.js";
import disfoService from "services/report/disfoService.js";

export default function DisplayBoard() {
  const [piketList, setPiketList] = useState([]);
  const [disfoList, setDisfoList] = useState([]);
  const [runningText, setRunningText] = useState("");
  const [time, setTime] = useState(new Date());
  const [activeMediaIdx, setActiveMediaIdx] = useState(0);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      const [rPiket, rDisfo, rRun] = await Promise.all([
        PiketService.getJadwalHariIni(),
        disfoService.getPublicContent(),
        disfoService.getPublicRunningText(),
      ]);
      setPiketList(rPiket?.data?.data || []);
      setDisfoList(rDisfo?.data?.data || []);
      const runTextData = rRun?.data?.data?.[0];
      setRunningText(runTextData?.teks || "Selamat Datang di Papan Informasi Digital");
    } catch (e) {
      console.error("Gagal load data display", e);
    } finally {
      setLoading(false);
    }
  }, []);

  // Timer jam & auto-refresh 30s
  useEffect(() => {
    loadData();
    const clockTimer = setInterval(() => setTime(new Date()), 1000);
    const refreshTimer = setInterval(() => loadData(), 30000);
    return () => {
      clearInterval(clockTimer);
      clearInterval(refreshTimer);
    };
  }, [loadData]);

  // Rotasi media/video/gambar setiap N detik
  useEffect(() => {
    if (!disfoList.length) return;
    const currentDur = disfoList[activeMediaIdx]?.durasi_detik || 10;
    const mediaTimer = setTimeout(() => {
      setActiveMediaIdx((prev) => (prev + 1) % disfoList.length);
    }, currentDur * 1000);
    return () => clearTimeout(mediaTimer);
  }, [disfoList, activeMediaIdx]);

  const activeMedia = disfoList[activeMediaIdx];

  return (
    <Box
      w="100vw"
      h="100vh"
      bg="gray.900"
      color="white"
      overflow="hidden"
      position="fixed"
      top="0"
      left="0"
      zIndex="9999"
      display="flex"
      flexDirection="column"
    >
      {/* Header */}
      <Flex bg="blue.900" px={6} py={4} align="center" justify="space-between" boxShadow="md">
        <HStack spacing={4}>
          <Heading size="lg" color="white">PAPAN INFORMASI DIGITAL</Heading>
          <Badge colorScheme="green" fontSize="1em" px={3} py={1} borderRadius="md">
            LIVE
          </Badge>
        </HStack>
        <VStack align="flex-end" spacing={0}>
          <Text fontSize="2xl" fontWeight="bold">
            {time.toLocaleTimeString("id-ID")}
          </Text>
          <Text fontSize="sm" color="gray.300">
            {time.toLocaleDateString("id-ID", {
              weekday: "long",
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </Text>
        </VStack>
      </Flex>

      {/* Main Content */}
      <Grid templateColumns="1fr 2fr" gap={4} p={4} flex="1" overflow="hidden">
        {/* Left Side: Petugas Piket Hari Ini */}
        <GridItem bg="gray.800" borderRadius="lg" p={4} display="flex" flexDirection="column" boxShadow="lg">
          <Heading size="md" mb={3} color="blue.300" borderBottom="2px solid" borderColor="blue.500" pb={2}>
            PETUGAS PIKET HARI INI
          </Heading>
          {loading ? (
            <Center flex="1"><Spinner size="xl" /></Center>
          ) : piketList.length === 0 ? (
            <Center flex="1">
              <Text color="gray.400" fontSize="lg">Belum ada jadwal piket hari ini</Text>
            </Center>
          ) : (
            <VStack align="stretch" spacing={3} overflowY="auto" flex="1">
              {piketList.map((p) => (
                <Box key={p.id} bg="gray.700" p={3} borderRadius="md" borderLeft="4px solid" borderColor="teal.400">
                  <HStack justify="space-between">
                    <Text fontWeight="bold" fontSize="lg">{p.nama || p.nip}</Text>
                    <Badge colorScheme="teal">{p.peran || "Anggota"}</Badge>
                  </HStack>
                  <Text fontSize="sm" color="gray.300">NIP: {p.nip}</Text>
                  <Text fontSize="sm" color="blue.200">Regu: {p.regu?.nama_regu || "-"}</Text>
                </Box>
              ))}
            </VStack>
          )}
        </GridItem>

        {/* Right Side: Media Display (Video/Gambar/Konten Disfo) */}
        <GridItem bg="black" borderRadius="lg" overflow="hidden" display="flex" flexDirection="column" position="relative">
          {activeMedia ? (
            <Box flex="1" display="flex" alignItems="center" justifyContent="center" position="relative">
              {activeMedia.tipe === "video" ? (
                <video
                  src={activeMedia.file_url}
                  autoPlay
                  loop
                  muted
                  style={{ width: "100%", height: "100%", objectFit: "contain" }}
                />
              ) : activeMedia.tipe === "gambar" ? (
                <Image src={activeMedia.file_url} alt={activeMedia.judul} maxH="100%" maxW="100%" objectFit="contain" />
              ) : (
                <VStack p={8} textWrap="wrap" textAlign="center">
                  <Heading size="xl" color="yellow.300">{activeMedia.judul}</Heading>
                  <Text fontSize="2xl" color="white" mt={4}>{activeMedia.isi_teks}</Text>
                </VStack>
              )}
              <Box position="absolute" bottom="10px" left="10px" bg="blackAlpha.700" px={3} py={1} borderRadius="md">
                <Text fontSize="sm" color="white">{activeMedia.judul}</Text>
              </Box>
            </Box>
          ) : (
            <Center flex="1">
              <VStack spacing={3}>
                <Heading size="lg" color="gray.500">DISFO DISPLAY</Heading>
                <Text color="gray.400">Tidak ada media aktif</Text>
              </VStack>
            </Center>
          )}
        </GridItem>
      </Grid>

      {/* Footer: Running Text */}
      <Box bg="yellow.500" color="black" py={2} overflow="hidden" whiteSpace="nowrap" position="relative">
        <Box
          display="inline-block"
          pl="100%"
          animation="marquee 25s linear infinite"
          style={{
            animation: "marquee 25s linear infinite",
          }}
        >
          <Text fontSize="xl" fontWeight="bold">
            📢 {runningText}
          </Text>
        </Box>
        <style>{`
          @keyframes marquee {
            0% { transform: translate(0, 0); }
            100% { transform: translate(-100%, 0); }
          }
        `}</style>
      </Box>
    </Box>
  );
}
